import uuid from 'uuid/v4';
import AnalyticsTypes from '~/constants/types/AnalyticsTypes';
import QuoteTypes from '~/constants/types/QuoteTypes';
import {createAction} from 'redux-actions';
import Immutable from 'immutable';
import cloneDeep from 'lodash/cloneDeep';

import remoteServices from '~/constants/RemoteServices';
import {URLS} from '~/constants/QuoteConstants';
import {postHeaders, rfeCredentialsHeader} from '~/utils/http';
import {
  isBulk,
  viewWithMoreThanThreeColors,
  getNumberOfProductViewsWithColor,
  removeView
} from '~/utils/quote';

import {isBlank} from '~/selectors/CanvasSelectors';
import {getCompositeId} from '~/selectors/DesignSelectors';
import {getNamesAndNumbersSizes} from '~/selectors/NamesAndNumbersSelectors';
import {
  anchorColorCount,
  getCurrentColorId,
  getAnchorStyle,
  getProductViews
} from '~/selectors/ProductSelectors';
import {
  hasQuantitiesForAllProducts,
  isFetchingPricesWithoutDroppedView,
  getItemizedPrices,
  getSleeveViewLessPrice,
  getTotalPrice,
  getTotalQuantity
} from '~/selectors/QuoteSelectors';

import {
  deserializePriceQuote,
  deserializeQuoteWidget,
  deserializeOnlyItemizedPricesFromPriceQuote
} from '~/data/deserializers/quote';
import {serialize} from '~/data/serializers/quote';
import {setEstimatedQty} from '~/slices/priceSlice';
import rollbar from '~/vendor/rollbar';

let lastRealTimePriceRequestId;

export const updateColorfulQuote = createAction(
  QuoteTypes.UPDATE_COLORFUL_QUOTE
);

export const updateColorCount = createAction(QuoteTypes.UPDATE_COLOR_COUNT);

export const updateNcoEligibility = createAction(
  QuoteTypes.UPDATE_NCO_ELIGIBILITY
);

export const updateUnavailableCoreSizes = createAction(
  QuoteTypes.UPDATE_UNAVAILABLE_CORE_SIZES
);

export const updateDroppedView = payload => dispatch => {
  dispatch({
    type: QuoteTypes.UPDATE_DROPPED_VIEW,
    payload: payload
  });
};

export const resetItemizedPricesWithoutDroppedView = () => dispatch => {
  dispatch(updateItemizedPricesWithoutDroppedView(null));
};

export const updateSleeveViewLessSaveAmount = () => (dispatch, getState) => {
  const state = getState();
  const totalPrice = getTotalPrice(state);
  const sleeveViewLessPrice = getSleeveViewLessPrice(state);

  if (
    typeof sleeveViewLessPrice !== 'number' ||
    typeof totalPrice !== 'number'
  ) {
    return;
  }

  const saveAmount = Math.round((1 - sleeveViewLessPrice / totalPrice) * 100);

  dispatch({
    type: QuoteTypes.UPDATE_SLEEVE_VIEW_LESS_SAVE_AMOUNT,
    payload: saveAmount > 0 ? saveAmount : null
  });
};

export const updateHasFullQuote = createAction(
  QuoteTypes.UPDATE_HAS_FULL_QUOTE
);

export const updateIsFetchingPricesWithoutDroppedView = createAction(
  QuoteTypes.UPDATE_IS_FETCHING_PRICES_WITHOUT_DROPPED_VIEW
);

export const updateIsInventoryShippingType = createAction(
  QuoteTypes.UPDATE_IS_INVENTORY_SHIPPING_TYPE
);

export const updateOneColorLessPrice = createAction(
  QuoteTypes.UPDATE_ONE_COLOR_LESS_PRICE
);

export const updateOneViewLessPrice = createAction(
  QuoteTypes.UPDATE_ONE_VIEW_LESS_PRICE
);

export const updateSleeveViewLessPrice = createAction(
  QuoteTypes.UPDATE_SLEEVE_VIEW_LESS_PRICE
);

export const updateEmbroideryToPrintPrice = createAction(
  QuoteTypes.UPDATE_EMBROIDERY_TO_PRINT_PRICE
);

export const updatePostalCode = createAction(QuoteTypes.UPDATE_POSTAL_CODE);

export const updateProductQuantities = quantities => (dispatch, getState) => {
  dispatch({
    type: QuoteTypes.UPDATE_PRODUCT_QUANTITIES,
    payload: quantities
  });
  const state = getState();
  const totalQty = getTotalQuantity(state);

  // need to keep estimated qty in sync with total qty when navigating backwards 💀
  dispatch(setEstimatedQty(totalQty));
};

export const updateQuote = createAction(QuoteTypes.UPDATE_QUOTE);

export const updateQuoteBlank = createAction(QuoteTypes.UPDATE_QUOTE_BLANK);

export const updateQuoteMode = createAction(QuoteTypes.UPDATE_MODE);

export const updateShipping = createAction(QuoteTypes.UPDATE_SHIPPING);

export const updateTotalPrice = createAction(QuoteTypes.UPDATE_TOTAL_PRICE);

export const updateRealTimeTotalPrice = createAction(
  QuoteTypes.UPDATE_REAL_TIME_TOTAL_PRICE
);

export const updateItemizedPrices = createAction(
  QuoteTypes.UPDATE_ITEMIZED_PRICES
);

export const updateItemizedPricesWithoutDroppedView = createAction(
  QuoteTypes.UPDATE_ITEMIZED_PRICES_WITHOUT_DROPPED_VIEW
);

export const updateRealTimeItemizedPrices = createAction(
  QuoteTypes.UPDATE_REAL_TIME_ITEMIZED_PRICES
);

export const showShippingForm = createAction(QuoteTypes.SHOW_SHIPPING_FORM);

export const realTimePricingSuccess = createAction(
  QuoteTypes.REAL_TIME_PRICING_SUCCESS
);

export const realTimePricingError = createAction(
  QuoteTypes.REAL_TIME_PRICING_ERROR
);

export function updateQuantitiesFromNamesAndNumbers() {
  return (dispatch, getState) => {
    const state = getState();
    const namesAndNumbersSizes = getNamesAndNumbersSizes(state);
    const productKey = getCurrentColorId(state);
    const quantities = {};
    quantities[productKey] = namesAndNumbersSizes;

    dispatch({
      type: QuoteTypes.UPDATE_PRODUCT_QUANTITIES,
      payload: quantities,
      meta: {
        source: 'NAMES_NUMBERS'
      }
    });
  };
}

export const updateProductQuantitiesWithoutSortingSelection =
  quantities => dispatch => {
    dispatch({
      type: QuoteTypes.UPDATE_PRODUCT_QUANTITIES,
      payload: quantities,
      meta: {
        dontSortSelectionByQuantity: true
      }
    });
  };

function checkResponseOkay(response) {
  return response.ok ? response : Promise.reject(response);
}

function handleRealTimePriceResponse(dispatch, metadata = {}) {
  return data => {
    if (data.errors) {
      return Promise.reject(data);
    }
    if (data.totalPrice) {
      if (metadata.realTimePriceRequestId === lastRealTimePriceRequestId) {
        dispatch(realTimePricingSuccess());
        dispatch(updateColorfulQuote(metadata.isColorful));
        dispatch(updateRealTimeTotalPrice({totalPrice: data.totalPrice}));
        dispatch(updateRealTimeItemizedPrices(data.itemizedPrices));
        dispatch({type: AnalyticsTypes.REAL_TIME_PRICE_CHANGE});
      }
      return data;
    }
    return Promise.reject(data);
  };
}

export function updateRealTimePrice() {
  return (dispatch, getState) => {
    const id = uuid();
    lastRealTimePriceRequestId = id;
    const state = getState();
    const isColorful = !isBlank(state);
    const isSavedDesign = !!getCompositeId(state);
    const selectedProductId = getCurrentColorId(state);

    const postBody = serialize(getState(), {
      useTotalQuantity: !isSavedDesign && !hasQuantitiesForAllProducts(state),
      useZipCodeGuess: true
    });

    return window
      .fetch(remoteServices.price_quote, {
        ...postHeaders,
        credentials: rfeCredentialsHeader,
        body: postBody
      })
      .then(checkResponseOkay)
      .then(response => response.json())
      .then(data => data.quote)
      .then(deserializePriceQuote)
      .then(data => {
        return {
          ...data,
          itemizedPrices: Immutable.Map({
            [selectedProductId]: data.itemizedPrices.get(selectedProductId)
          })
        };
      })
      .then(
        handleRealTimePriceResponse(dispatch, {
          isColorful,
          realTimePriceRequestId: id
        })
      )
      .catch(error =>
        dispatch(realTimePricingError({error, selectedProductId}))
      );
  };
}

export function fetchInternationalPrice() {
  return (dispatch, getState) => {
    const state = getState();
    const postBody = serialize(state, {
      isVolumeDiscountFlag: true,
      overrideZipCode: true,
      zipCodeParam: '19901'
    });

    const controller = new AbortController();
    const signal = controller.signal;

    const fetchPromise = window
      .fetch(URLS.quoting, {
        ...postHeaders,
        body: postBody,
        credentials: rfeCredentialsHeader,
        timeout: 60000,
        signal
      })
      .then(checkResponseOkay)
      .then(response => response.json());

    fetchPromise.abort = () => {
      controller.abort();
    };

    fetchPromise.catch(error => {
      if (error.name === 'AbortError') {
        return;
      }
      rollbar.error('Failed to fetch international quote', error);
    });

    return fetchPromise;
  };
}

export const fetchQuote = (state, config) => {
  const defaultConfig = {
    type: 'fullQuote'
  };
  const {colorCounts, type} = {...defaultConfig, ...config};
  const typeMap = {
    fullQuote: {
      deserializer: deserializeQuoteWidget,
      isVolumeDiscountFlag: true,
      useZipCodeGuess: true,
      url: URLS.quoting
    },
    priceQuote: {
      deserializer: deserializePriceQuote,
      isVolumeDiscountFlag: false,
      responseRoot: 'quote',
      url: remoteServices.price_quote
    }
  };
  const {
    deserializer,
    isVolumeDiscountFlag,
    responseRoot,
    url,
    useZipCodeGuess
  } = typeMap[type] || typeMap[defaultConfig.type];

  const requestConfig = {isVolumeDiscountFlag, useZipCodeGuess};

  if (colorCounts) {
    requestConfig.colorCounts = colorCounts;
  }

  return window
    .fetch(url, {
      ...postHeaders,
      body: serialize(state, requestConfig),
      credentials: rfeCredentialsHeader,
      timeout: 60000
    })
    .then(checkResponseOkay)
    .then(response => response.json())
    .then(data =>
      responseRoot ? deserializer(data[responseRoot]) : deserializer(data)
    );
};

export const getQuote = () => (dispatch, getState) => {
  const state = getState();
  dispatch(updateHasFullQuote(false));
  return fetchQuote(state, {type: 'fullQuote'}).then(data => {
    dispatch(updateQuote(data));
    dispatch(
      updateTotalPrice({
        totalPrice: data.totalPrice,
        volumeDiscountPrice: data.volumeDiscountPrice
      })
    );
    dispatch(updateItemizedPrices(data.itemizedPrices));
    dispatch(updateShipping(data.shipping));
    dispatch(updateHasFullQuote(true));
    return data;
  });
};

export const getPriceQuote = () => (dispatch, getState) => {
  const state = getState();
  fetchQuote(state, {type: 'priceQuote'}).then(data => {
    dispatch(
      updateTotalPrice({
        totalPrice: data.totalPrice,
        volumeDiscountPrice: data.volumeDiscountPrice
      })
    );
    dispatch(updateItemizedPrices(data.itemizedPrices));
  });
};

export const fetchOneColorLessPrice = () => (dispatch, getState) => {
  const state = getState();
  const colorCount = anchorColorCount(state);
  const reducedColorCount = {...colorCount};
  const totalQuantity = getTotalQuantity(state);

  if (isBulk(totalQuantity) && !!viewWithMoreThanThreeColors(colorCount)) {
    // find the view with the most colors
    let viewMaxColor = viewWithMoreThanThreeColors(reducedColorCount);
    Object.keys(reducedColorCount)
      .filter(view => reducedColorCount[view] >= 3)
      .map(view => {
        if (reducedColorCount[view] > reducedColorCount[viewMaxColor]) {
          viewMaxColor = view;
        }
      });

    // remove a color from that view
    reducedColorCount[viewMaxColor] = reducedColorCount[viewMaxColor] - 1;

    fetchQuote(state, {colorCounts: reducedColorCount, type: 'priceQuote'})
      .then(deserializePriceQuote)
      .then(data => {
        dispatch(updateOneColorLessPrice(data.totalPrice));
      });
  } else {
    dispatch(updateOneColorLessPrice());
  }
};

export const fetchOneViewLessPrice = () => (dispatch, getState) => {
  const state = getState();
  const colorCount = anchorColorCount(state);

  if (getNumberOfProductViewsWithColor({colorCounts: colorCount}) < 2) {
    return dispatch(updateOneViewLessPrice());
  }

  let viewColorCounts = {...colorCount};
  let viewLeastColor = Object.keys(colorCount)[0];

  // find the view with the least colors
  Object.keys(viewColorCounts).map(view => {
    if (viewColorCounts[view] < viewColorCounts[viewLeastColor]) {
      viewLeastColor = view;
    }
  });

  viewColorCounts = removeView({
    views: viewColorCounts,
    removeViewName: viewLeastColor
  });

  fetchQuote(state, {colorCounts: viewColorCounts, type: 'priceQuote'})
    .then(deserializePriceQuote)
    .then(data => {
      dispatch(updateOneViewLessPrice(data.totalPrice));
    });
};

export const fetchPriceWithoutDroppedView =
  () => async (dispatch, getState) => {
    const state = getState();
    const anchorStyle = getAnchorStyle(state);
    const productViews = getProductViews(state)(anchorStyle);
    const colorCount = anchorColorCount(state);
    const itemizedPricesBeforeDropView = getItemizedPrices(state);

    if (
      isFetchingPricesWithoutDroppedView(state) ||
      !anchorStyle ||
      !productViews ||
      !colorCount ||
      !itemizedPricesBeforeDropView
    ) {
      return;
    }

    dispatch(updateIsFetchingPricesWithoutDroppedView(true));
    resetItemizedPricesWithoutDroppedView();

    const viewsColorCounts = getQuotableViewsColorCounts({
      productViews,
      colorCount
    });
    const viewsNames = Object.keys(viewsColorCounts);
    const fetchPricesDropViewResults = [];

    for (let i = 0; i < viewsNames.length; i++) {
      const droppedViewName = viewsNames[i];
      const colorCountsWithoutDroppedView = viewsColorCounts[droppedViewName];
      fetchPricesDropViewResults.push(
        fetchQuote(state, {
          colorCounts: colorCountsWithoutDroppedView,
          type: 'priceQuote'
        })
          .then(deserializeOnlyItemizedPricesFromPriceQuote)
          .then(itemizedPricesAfterDropView => {
            dispatch(
              updateItemizedPricesWithoutDroppedView(
                buildItemizedPricesWithoutDroppedView({
                  itemizedPricesBefore: itemizedPricesBeforeDropView,
                  itemizedPricesAfter: itemizedPricesAfterDropView,
                  droppedViewName: droppedViewName
                })
              )
            );
          })
      );
    }
    await Promise.all(fetchPricesDropViewResults);
    return dispatch(updateIsFetchingPricesWithoutDroppedView(false));
  };

const getQuotableViewsColorCounts = ({productViews, colorCount}) => {
  if (!productViews || !colorCount) return;

  const quotableViewsColorCounts = {};
  productViews.forEach(view => {
    const droppedViewName = view?.name;

    if (
      droppedViewName &&
      getNumberOfProductViewsWithColor({colorCounts: colorCount}) > 0
    ) {
      const viewColorCount = removeView({
        views: cloneDeep(colorCount),
        removeViewName: droppedViewName
      });
      quotableViewsColorCounts[droppedViewName] = viewColorCount;
    }
  });

  return quotableViewsColorCounts;
};

const buildItemizedPricesWithoutDroppedView = ({
  itemizedPricesBefore,
  itemizedPricesAfter,
  droppedViewName
}) => {
  const itemizedPricesWithoutDroppedView = {};
  const {
    fullItemizedPricesAfterDropView,
    sumSubtotalsBefore,
    sumSubtotalsAfter
  } = getFullItemizedPricesAfterDropView({
    itemizedPricesBefore,
    itemizedPricesAfter
  });
  itemizedPricesWithoutDroppedView[droppedViewName] = Immutable.Map({
    itemizedPrices:
      fullItemizedPricesAfterDropView || itemizedPricesWithoutDroppedView,
    sumSubtotalsBefore: sumSubtotalsBefore,
    sumSubtotalsAfter: sumSubtotalsAfter
  });

  return itemizedPricesWithoutDroppedView;
};

const getFullItemizedPricesAfterDropView = ({
  itemizedPricesBefore,
  itemizedPricesAfter
}) => {
  if (
    !itemizedPricesBefore ||
    !itemizedPricesAfter ||
    !itemizedPricesBefore.toJS ||
    !itemizedPricesAfter.toJS ||
    !itemizedPricesBefore.toJS() ||
    !itemizedPricesAfter.toJS()
  ) {
    return null;
  }
  const itemPricesBefore = itemizedPricesBefore.toJS();
  const itemPricesAfter = itemizedPricesAfter.toJS();

  if (!itemPricesBefore || !itemPricesAfter || !Object.keys(itemPricesBefore)) {
    return null;
  }

  const productIds = Object.keys(itemPricesBefore);

  const fullItemPrices = {};
  let sumSubtotalsBefore = 0;
  let sumSubtotalsAfter = 0;

  productIds.forEach(productId => {
    const priceBefore = itemPricesBefore[productId];
    const priceAfter = itemPricesAfter[productId];

    if (
      priceBefore?.itemPrice &&
      priceBefore?.subtotal &&
      priceAfter?.itemPrice &&
      priceAfter?.subtotal
    ) {
      sumSubtotalsBefore += priceBefore.subtotal;
      sumSubtotalsAfter += priceAfter.subtotal;

      fullItemPrices[productId] = Immutable.Map({
        oldItemPrice: priceBefore.itemPrice,
        oldSubtotal: priceBefore.subtotal,
        itemPrice: priceAfter.itemPrice,
        subtotal: priceAfter.subtotal
      });
    }
  });

  return {
    fullItemizedPricesAfterDropView: Immutable.Map(fullItemPrices),
    sumSubtotalsBefore: sumSubtotalsBefore,
    sumSubtotalsAfter: sumSubtotalsAfter
  };
};

export const fetchSleeveViewLessPrice = () => (dispatch, getState) => {
  const state = getState();
  const colorCount = anchorColorCount(state);

  const shouldMakePriceQuoteCall =
    colorCount['rightSleeve'] > 0 ||
    colorCount['leftSleeve'] > 0 ||
    (colorCount['sleeve'] && colorCount['sleeve'][0] > 0) ||
    (colorCount['sleeve'] && colorCount['sleeve'][1] > 0);

  if (!shouldMakePriceQuoteCall) {
    return dispatch(updateSleeveViewLessPrice());
  }

  const removedView = {...colorCount};
  delete removedView['rightSleeve'];
  delete removedView['leftSleeve'];
  delete removedView['sleeve'];

  fetchQuote(state, {colorCounts: removedView, type: 'priceQuote'})
    .then(deserializePriceQuote)
    .then(data => {
      dispatch(updateSleeveViewLessPrice(data.totalPrice));
    });
};

export const setSwappedPrintMethod = createAction(
  QuoteTypes.SET_SWAPPED_PRINT_METHOD
);
